import type { ReactNode } from "react";
import { Text } from "../../atoms/Text/Text";
import { cn } from "../../lib/cn";

export type EmptyStateIllustration = "documents" | "noData";

// Both SVGs are unDraw illustrations (undraw.co), recolored to the design
// system's accent-600 (#0E6B5C) — see public/illustrations/ in apps/app.
// "documents" is the no-records-in-a-list case (DataTable's emptyState,
// generously sized so the table doesn't collapse to a single line of muted
// text); "noData" is the smaller dashed-border "nothing configured yet"
// case (e.g. no branches to pick from).
const ILLUSTRATION_SRC: Record<EmptyStateIllustration, string> = {
  documents: "/illustrations/documents.svg",
  noData: "/illustrations/no-data.svg",
};

const ILLUSTRATION_HEIGHT: Record<EmptyStateIllustration, string> = {
  documents: "h-40",
  noData: "h-32",
};

export interface EmptyStateProps {
  illustration?: EmptyStateIllustration;
  children: ReactNode;
  className?: string;
  // Dashed-border boxed treatment, matching the "noData" use case's own
  // convention (OnboardingPage's no-branches-available state) — tighter
  // padding since it's usually inline within a form, not a full table.
  bordered?: boolean;
}

// Consistent no-record/no-data empty state — same illustration size and
// spacing everywhere a DataTable/DataList's emptyState (or an equivalent
// inline empty case) needs one, so tables never collapse to a single line
// of muted text and every empty state has the same predictable footprint.
export function EmptyState({ illustration = "documents", children, className = "", bordered = false }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-3 py-12 text-center",
        bordered && "rounded-md border border-dashed border-border py-8",
        className
      )}
    >
      <img
        src={ILLUSTRATION_SRC[illustration]}
        alt=""
        aria-hidden="true"
        className={cn(ILLUSTRATION_HEIGHT[illustration], "w-auto")}
      />
      <Text as="p" size="sm" variant="muted" className="max-w-sm">
        {children}
      </Text>
    </div>
  );
}
